<template>
  <div class="ticket-detail-page">
    <div class="content">
      <router-link to="/tickets" class="back-link">← My Tickets</router-link>

      <div v-if="loading" class="state-msg">Loading ticket…</div>
      <div v-else-if="error" class="state-msg state-msg--error">{{ error }}</div>

      <div v-else-if="ticket" class="ticket-card">
        <div v-if="ticket.eventImageUrl" class="ticket-card__banner">
          <img :src="ticket.eventImageUrl" alt="" class="ticket-card__banner-img" />
        </div>

        <div class="ticket-card__body">
          <div class="ticket-card__event-meta">
            <span class="ticket-card__date">{{ formatDateRange(ticket.eventDate, ticket.eventEndDate) }}</span>
            <span v-if="ticket.eventVenue" class="ticket-card__venue">· {{ ticket.eventVenue }}</span>
          </div>
          <h1 class="ticket-card__event-title">{{ ticket.eventTitle }}</h1>

          <div class="ticket-card__divider"></div>

          <div class="ticket-card__grid">
          <div class="ticket-card__info">
          <div class="ticket-card__details">
            <div class="detail-row">
              <span class="detail-row__label">Tier</span>
              <span class="detail-row__value">{{ ticket.tierName }}</span>
            </div>
            <div v-if="ticket.nftRider" class="detail-row">
              <span class="detail-row__label">Rider</span>
              <span class="detail-row__value">{{ ticket.nftRider }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-row__label">Ticket ID</span>
              <span class="detail-row__value">#{{ ticket.id }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-row__label">Status</span>
              <span
                class="detail-row__badge"
                :class="isUsed(ticket) ? 'detail-row__badge--used' : 'detail-row__badge--valid'"
              >{{ isUsed(ticket) ? 'Used' : 'Not used' }}</span>
            </div>
            <div class="detail-row" v-if="ticket.explorerUrl">
              <span class="detail-row__label">NFT</span>
              <a :href="ticket.explorerUrl" target="_blank" rel="noopener noreferrer" class="detail-row__explorer-link">View on blockchain ↗</a>
            </div>
          </div>

          </div>

          <div class="ticket-card__aside">
          <template v-if="hasOwnNftArt">
            <div class="ticket-card__divider ticket-card__divider--aside"></div>
            <div class="nft-section">
              <img
                :src="ticket.nftImageUrl"
                :alt="ticket.nftRider ? `${ticket.nftRider} rider card` : 'NFT artwork'"
                class="nft-section__img"
              />
            </div>
          </template>

          <div class="ticket-card__divider ticket-card__divider--qr"></div>

          <!-- QR section -->
          <div v-if="!qrData.qrBase64 && !qrLoading" class="qr-section">
            <button class="btn-primary" @click="loadQr">Show QR code</button>
          </div>

          <div v-if="qrLoading" class="qr-section">
            <div class="qr-placeholder">Generating QR…</div>
          </div>

          <div v-if="qrData.qrBase64" class="qr-section">
            <div class="qr-wrap">
              <img :src="`data:image/png;base64,${qrData.qrBase64}`" alt="QR code" class="qr-img" />
            </div>
            <p class="qr-code-text">{{ qrData.qrCode }}</p>
            <p class="qr-hint">Show this at the door or save it.</p>
            <a
              :href="`data:image/png;base64,${qrData.qrBase64}`"
              :download="`ticket-qr-${qrData.qrCode}.png`"
              class="btn-secondary"
            >Save QR code</a>
          </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ticketingApi from '@/api/ticketing'

export default {
  name: 'TicketDetail',
  data() {
    return {
      ticket: null,
      loading: true,
      error: null,
      qrData: {},
      qrLoading: false,
    }
  },
  async created() {
    const id = parseInt(this.$route.params.id, 10)
    try {
      const res = await ticketingApi.getMyTickets()
      const tickets = res.data || res
      this.ticket = tickets.find((t) => t.id === id) || null
      if (!this.ticket) this.error = 'Ticket not found.'
    } catch {
      this.error = 'We couldn’t load your ticket. Please try again.'
    } finally {
      this.loading = false
    }
  },
  computed: {
    // Tiers without their own NFT art fall back to the event hero image, which
    // the banner already shows — only render art that's the ticket's own.
    hasOwnNftArt() {
      return !!this.ticket?.nftImageUrl && this.ticket.nftImageUrl !== this.ticket.eventImageUrl
    },
  },
  methods: {
    // Mirrors the scanner: a checked-out ticket can't get back in unless the
    // event allows re-entry, so it's spent rather than "Not used".
    isUsed(ticket) {
      return ticket.status === 'checked_in' ||
        (ticket.status === 'checked_out' && !ticket.eventAllowReEntry)
    },
    async loadQr() {
      this.qrLoading = true
      try {
        const res = await ticketingApi.generateQr(this.ticket.id)
        this.qrData = res.data || res
      } catch {
        alert('We couldn’t load your QR code. Please try again.')
      } finally {
        this.qrLoading = false
      }
    },
    formatDateRange(start, end) {
      if (!start) return ''
      const opts = { month: 'short', day: 'numeric', year: 'numeric' }
      const s = new Date(start).toLocaleDateString(undefined, opts)
      if (!end) return s
      const e = new Date(end).toLocaleDateString(undefined, opts)
      return `${s} – ${e}`
    },
  },
}
</script>

<style scoped lang="scss">
$bg: #f0f0f5;
$surface: #ffffff;
$text: #0a0a0a;
$muted: rgba(10,10,10,0.5);
$radius-lg: 24px;
$radius-pill: 44px;
$page-px: 80px;
$font: 'Inter', sans-serif;

* { box-sizing: border-box; }

.ticket-detail-page {
  font-family: $font;
  background: $bg;
  min-height: 100vh;
  color: $text;
}

.content {
  padding: 40px $page-px;
  max-width: 1120px;

  @media (max-width: 1080px) { padding: 32px 32px; }
  @media (max-width: 640px)  { padding: 24px 16px; }
}

.back-link {
  display: inline-block;
  font-size: 14px;
  font-weight: 700;
  color: $muted;
  text-decoration: none;
  margin-bottom: 24px;

  &:hover { color: $text; }
}

.state-msg {
  font-size: 18px;
  color: $muted;
  padding: 40px 0;
  &--error { color: #c0392b; }
}

.ticket-card {
  background: $surface;
  border-radius: $radius-lg;
  overflow: hidden;

  &__banner {
    width: 100%;
    aspect-ratio: 16/7;
    overflow: hidden;
  }

  &__banner-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__body {
    padding: 28px 32px 36px;

    @media (max-width: 640px) { padding: 20px 20px 28px; }
  }

  &__event-meta {
    font-size: 13px;
    color: $muted;
    margin-bottom: 6px;
  }

  &__date { font-weight: 600; }
  &__venue { margin-left: 4px; }

  &__event-title {
    font-size: 26px;
    font-weight: 800;
    margin: 0 0 20px;
    line-height: 1.25;

    @media (max-width: 640px) { font-size: 20px; }
  }

  &__divider {
    border: none;
    border-top: 1px solid rgba(0,0,0,0.07);
    margin: 20px 0;
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  // Desktop: details on the left, NFT art + QR on the right.
  @media (min-width: 900px) {
    &__banner { aspect-ratio: 21/7; }

    &__body { padding: 36px 48px 48px; }

    &__event-title { font-size: 34px; }

    &__grid {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 48px;
      align-items: start;
    }

    &__aside { display: flex; flex-direction: column; gap: 24px; }

    &__divider--aside { display: none; }
    &__divider--qr { display: none; }
  }
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 12px;

  &__label {
    font-size: 13px;
    font-weight: 700;
    color: $muted;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    width: 80px;
    flex-shrink: 0;
  }

  &__value {
    font-size: 15px;
    font-weight: 600;
    color: $text;
  }

  &__badge {
    font-size: 12px;
    font-weight: 700;
    padding: 3px 12px;
    border-radius: $radius-pill;

    &--valid { background: #d4edda; color: #155724; }
    &--used  { background: #fff3cd; color: #856404; }
  }

  &__explorer-link {
    font-size: 14px;
    font-weight: 700;
    color: #6d28d9;
    text-decoration: none;

    &:hover { text-decoration: underline; }
  }
}

.nft-section {
  display: flex;
  justify-content: center;

  &__img {
    display: block;
    width: 100%;
    max-width: 320px;
    aspect-ratio: 4 / 5;
    object-fit: cover;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  }
}

.qr-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.qr-placeholder {
  font-size: 15px;
  color: $muted;
  padding: 20px 0;
}

.qr-wrap {
  background: #f8f8fa;
  border-radius: 20px;
  padding: 16px;
  display: inline-flex;
}

.qr-img {
  width: 220px;
  height: 220px;
  object-fit: contain;
}

.qr-code-text {
  font-family: monospace;
  font-size: 13px;
  color: $muted;
  letter-spacing: 0.1em;
  margin: 0;
}

.qr-hint {
  font-size: 13px;
  color: $muted;
  margin: 0;
  text-align: center;
}

.btn-primary {
  background: $text;
  color: #fff;
  border: none;
  border-radius: $radius-pill;
  padding: 14px 32px;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;

  &:hover { opacity: 0.85; }
}

.btn-secondary {
  background: rgba(10,10,10,0.06);
  color: $text;
  border: 1px solid rgba(10,10,10,0.12);
  border-radius: $radius-pill;
  padding: 12px 28px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  text-align: center;
}
</style>
